// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import MainNavigationItem from "@insite/content-library/Components/MainNavigationItem";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { McMcHeaderContext } from "@mcmc/App/Pages/Header/HeaderContext";
import CaretDown from "@mcmc/XDL/Icons/CaretDown";
import { mergeWithCss, RobotoCondensed } from "@mcmc/XDL/Utilities";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    displayChangeCustomerLink: getSession(state).displayChangeCustomerLink,
});

export interface LogoTopMenusStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
    menusWrapper: InjectableCss;
    itemWrapper: InjectableCss;
    menuItem?: InjectableCss;
    menuItemIcon?: IconProps;
    menuItemTypography?: TypographyPresentationProps;
}

export interface StickyLogoTopMenusStyles {
    container: InjectableCss;
    logoWrapper: InjectableCss;
}

export const stickyLogoTopMenusStylesStyles: StickyLogoTopMenusStyles = {
    container: {
        css: css`
            margin-bottom: 0px;
        `,
    },
    logoWrapper: {
        css: css`
            ${StyledWrapper} {
                width: 50px;
                height: 50px;
            }
        `,
    },
};

export const logoTopMenusStylesStyles: LogoTopMenusStyles = {
    container: {
        css: css`
            display: flex;
            flex: 1;
            margin-bottom: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [null, null, stickyLogoTopMenusStylesStyles.container.css!], "max")}
        `,
    },
    logoWrapper: {
        css: css`
            margin-right: 20px;
            display: flex;
            align-items: center;
            justify-content: flex-start;

            ${StyledWrapper} {
                max-width: 100%;
                width: 265px;
                height: 70px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-right: 15px;
                        `,
                        stickyLogoTopMenusStylesStyles.logoWrapper.css!,
                    ],
                    "max",
                )}
        `,
    },
    menusWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            flex: 1;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemWrapper: {
        css: css`
            position: relative;
            overflow: visible;
            display: flex;
            margin-right: 15px;
            &:last-child {
                margin: 0;
            }
        `,
    },
    menuItem: {
        css: css`
            display: flex;
            align-items: center;
            height: 50px;
        `,
    },
    menuItemIcon: {
        css: css`
            margin-left: 5px;
        `,
        width: 11,
        height: 7,
        color: "primary",
        src: CaretDown,
    },
    menuItemTypography: {
        ...RobotoCondensed,
        variant: "headerSecondary",
        size: 16,
        color: "navigation.primary.text.main",
        css: css`
            letter-spacing: 0;
            font-weight: 500;
        `,
    },
};

type Props = PageProps & ReturnType<typeof mapStateToProps>;

const LogoTopMenus: React.FC<Props> = ({ id, displayChangeCustomerLink }) => {
    const { topMenus, isSticky } = React.useContext(McMcHeaderContext);
    const container = React.createRef<HTMLDivElement>();
    let styles = mergeWithCss(logoTopMenusStylesStyles, {});

    if (isSticky) {
        styles = mergeWithCss(logoTopMenusStylesStyles, stickyLogoTopMenusStylesStyles);
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.logoWrapper}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            {!isSticky && (
                <StyledWrapper {...styles.menusWrapper} ref={container}>
                    {topMenus!.map((link, index) => {
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <StyledWrapper key={index} {...styles.itemWrapper}>
                                <MainNavigationItem
                                    index={index}
                                    link={link}
                                    container={container}
                                    styles={styles as any}
                                    isOpen={false}
                                    displayChangeCustomerLink={displayChangeCustomerLink}
                                />
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const logoLinksSearch: WidgetModule = {
    component: connect(mapStateToProps)(LogoTopMenus),
    definition: {
        group: "Header",
        icon: "Filter",
    },
};

export default logoLinksSearch;
